/**
 * 全站 Header(_shared C-1)。标记/类名对齐 design/home.html(视觉真源)。
 * 登录态:用户菜单(头像+昵称 → 个人中心/订单/退出);未登录态:登录/注册入口。
 * 主导航受守卫项未登录点击 → 跳登录(useAuthRequired)。
 */
import { NavLink, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useAuthStore } from '@/store/auth';
import { useAuthRequired } from '@/router/useAuthRequired';

interface NavItem {
  to: string;
  label: string;
  /** 受守卫:未登录点击跳登录。 */
  guarded?: boolean;
}

// 主导航(对齐 _shared C-1 / index.md 信息架构)。发布/订单受守卫。
const NAV: NavItem[] = [
  { to: '/', label: '首页' },
  { to: '/search', label: '发现' },
  { to: '/publish', label: '发布商品', guarded: true },
  { to: '/orders', label: '我的订单', guarded: true },
];

export function Header() {
  const navigate = useNavigate();
  const requireAuth = useAuthRequired();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const clear = useAuthStore((s) => s.clear);

  const handleNav = (item: NavItem, e: React.MouseEvent) => {
    if (item.guarded && !isAuthenticated) {
      e.preventDefault();
      requireAuth();
    }
  };

  const handleGuarded = (to: string) => {
    requireAuth(() => navigate(to));
  };

  return (
    <header className="header">
      <div className="header-inner">
        <NavLink to="/" className="brand">
          我行
        </NavLink>

        <nav className="nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
              onClick={(e) => handleNav(item, e)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          className="header-search"
          onClick={() => navigate('/search')}
          aria-label="搜索"
        >
          <span>🔍</span>
          <input placeholder="搜技能、陪伴、达人…" readOnly tabIndex={-1} />
        </button>

        <div className="header-actions">
          <button
            type="button"
            className="msg-entry"
            title="消息"
            aria-label="消息"
            onClick={() => handleGuarded('/conversations')}
          >
            消息
          </button>

          {isAuthenticated ? (
            <UserMenu onLogout={clear} />
          ) : (
            <NavLink to="/login" className="btn btn-primary">
              登录 / 注册
            </NavLink>
          )}
        </div>
      </div>
    </header>
  );
}

function UserMenu({ onLogout }: { onLogout: () => void }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className="user-menu" aria-label="用户菜单">
          <span className="name">我的账户</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="dropdown-menu" sideOffset={8} align="end">
          <DropdownMenu.Item className="dropdown-item" onSelect={() => navigate('/me')}>
            个人中心
          </DropdownMenu.Item>
          <DropdownMenu.Item className="dropdown-item" onSelect={() => navigate('/seller-products')}>
            我的发布
          </DropdownMenu.Item>
          <DropdownMenu.Item className="dropdown-item" onSelect={() => navigate('/orders')}>
            我的订单
          </DropdownMenu.Item>
          <DropdownMenu.Item className="dropdown-item" onSelect={() => navigate('/earnings')}>
            我的收益
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="dropdown-sep" />
          <DropdownMenu.Item
            className="dropdown-item"
            onSelect={() => {
              onLogout();
              navigate('/');
            }}
          >
            退出登录
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
